import {
  useQuery,
  QueryKey,
  QueryObserverOptions,
  UseQueryResult,
  MutationKey,
  useMutation,
  MutateOptions,
} from "react-query";

import {
  titleListService,
  titleListShortService,
  titleDetailService,
  titleCreateService,
  titleUpdateService,
} from "./services";

type TitleListResponse = Awaited<ReturnType<typeof titleListService>>;
type TitleShortListResponse = Awaited<ReturnType<typeof titleListShortService>>;
type TitleDetailResponse = Awaited<ReturnType<typeof titleDetailService>>;

export const useTitles = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof titleListService>[0];
  options?: QueryObserverOptions<unknown, unknown, TitleListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "titleListHooks",
    queryFn: () => titleListService(params),
    ...options,
  });

  return response as UseQueryResult<TitleListResponse>;
};

export const useShortTitles = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof titleListShortService>[0];
  options?: QueryObserverOptions<unknown, unknown, TitleShortListResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "titleShortListHooks",
    queryFn: () => titleListShortService(params),
    ...options,
  });

  return response as UseQueryResult<TitleShortListResponse>;
};

export const useTitleDetail = ({
  queryKey,
  params,
  options,
}: {
  queryKey?: QueryKey | string;
  params: Parameters<typeof titleDetailService>[0];
  options?: QueryObserverOptions<unknown, unknown, TitleDetailResponse>;
}) => {
  const response = useQuery({
    queryKey: queryKey ?? "titleDetailHooks",
    queryFn: () => titleDetailService(params),
    ...options,
  });

  return response as UseQueryResult<TitleDetailResponse>;
};

export const useTitleUpdate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    TitleDetailResponse,
    unknown,
    Parameters<typeof titleUpdateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "titleUpdateHooks",
    mutationFn: titleUpdateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};

export const useTitleCreate = ({
  queryKey,
  options,
}: {
  queryKey?: MutationKey | string;
  options?: MutateOptions<
    TitleDetailResponse,
    unknown,
    Parameters<typeof titleCreateService>[0]
  >;
}) => {
  const response = useMutation({
    mutationKey: queryKey ?? "titleCreateHooks",
    mutationFn: titleCreateService,
    onSuccess: options?.onSuccess as unknown as () => void,
    onError: options?.onError as unknown as () => void,
  });

  return response;
};
